import { useState } from "react";
import {
  Hospital, Stethoscope, FlaskConical, Pill, DollarSign, RefreshCw,
  ChevronRight, ExternalLink, MapPin, X, AlertTriangle, CheckCircle2,
  Clock, Calculator, Calendar,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { formatNum } from "@/lib/constants";

const ETAPAS_AMBULATORIO = [
  { key: "Acceso", label: "Acceso", icon: Hospital, color: "#7C3AED", desc: "Búsqueda, agenda, confirmación",
    recomendaciones: ["Agendar hora por portal web o call center", "Confirmar cita 48h antes", "Llevar cédula de identidad"],
    alertaPendiente: "No tienes citas agendadas próximamente",
  },
  { key: "Financiero", label: "Financiero", icon: DollarSign, color: "#EC4899", desc: "Bono, copago previo a atención",
    recomendaciones: ["Solicitar bono electrónico IMED antes de la cita", "Verificar cobertura con tu ISAPRE", "Consultar convenios disponibles"],
    alertaPendiente: "No se registra pago previo para la próxima atención",
    estimable: true,
  },
  { key: "Atención", label: "Atención", icon: Stethoscope, color: "#3B82F6", desc: "Consulta médica",
    recomendaciones: ["Llegar 15 min antes para admisión", "Llevar exámenes previos", "Preparar lista de medicamentos actuales"],
    alertaPendiente: "No se registra consulta reciente",
  },
  { key: "Diagnóstica", label: "Diagnóstica", icon: FlaskConical, color: "#14B8A6", desc: "Lab, imagen, resultados",
    recomendaciones: ["Realizar exámenes indicados lo antes posible", "Ayuno de 8-12h para lab de sangre", "Llevar orden médica"],
    alertaPendiente: "Tienes exámenes indicados sin realizar",
    estimable: true,
  },
  { key: "Tratamiento", label: "Tratamiento", icon: Pill, color: "#F59E0B", desc: "Receta, farmacia, cirugía",
    recomendaciones: ["Retirar medicamentos en farmacia UC", "Seguir indicaciones de dosis y horario", "Consultar dudas con farmacéutico"],
    alertaPendiente: "Tienes indicaciones de tratamiento pendientes",
    estimable: true,
  },
  { key: "Seguimiento", label: "Seguimiento", icon: RefreshCw, color: "#6366F1", desc: "Control, CRM, re-agenda",
    recomendaciones: ["Agendar control según indicación médica", "Responder encuesta de satisfacción", "Mantener datos de contacto actualizados"],
    alertaPendiente: "No tienes control de seguimiento agendado",
  },
];

const ETAPAS_HOSPITALARIO = [
  { key: "Acceso", label: "Acceso", icon: Hospital, color: "#7C3AED", desc: "Indicación, presupuesto, agenda",
    recomendaciones: ["Confirmar presupuesto con tu ISAPRE", "Agendar exámenes pre-quirúrgicos", "Coordinar con CRM quirúrgico"],
    alertaPendiente: "No tienes presupuesto aprobado o pabellón agendado",
  },
  { key: "Atención", label: "Atención", icon: Stethoscope, color: "#3B82F6", desc: "Evaluación pre-op, cirugía",
    recomendaciones: ["Completar exámenes pre-quirúrgicos", "Seguir indicaciones de ayuno", "Llevar documentación completa"],
    alertaPendiente: "Evaluación pre-operatoria pendiente",
  },
  { key: "Diagnóstica", label: "Diagnóstica", icon: FlaskConical, color: "#14B8A6", desc: "Lab pre-op, imagen, biopsias",
    recomendaciones: ["Realizar exámenes pre-quirúrgicos indicados", "Llevar resultados a la evaluación", "Consultar resultados en portal"],
    alertaPendiente: "Exámenes pre-quirúrgicos pendientes",
    estimable: true,
  },
  { key: "Tratamiento", label: "Tratamiento", icon: Pill, color: "#F59E0B", desc: "Cirugía, hospitalización, farmacia",
    recomendaciones: ["Seguir indicaciones post-operatorias", "Retirar medicamentos indicados", "Reportar síntomas inusuales"],
    alertaPendiente: "Cirugía o tratamiento pendiente",
    estimable: true,
  },
  { key: "Financiero", label: "Financiero", icon: DollarSign, color: "#EC4899", desc: "Facturación, copago post-atención",
    recomendaciones: ["Revisar cuenta hospitalaria", "Solicitar detalle de facturación", "Gestionar reembolso con ISAPRE"],
    alertaPendiente: "Facturación pendiente de la hospitalización",
    estimable: true,
  },
  { key: "Seguimiento", label: "Seguimiento", icon: RefreshCw, color: "#6366F1", desc: "Control post-op, rehabilitación",
    recomendaciones: ["Agendar control post-quirúrgico (7-14 días)", "Seguir plan de rehabilitación", "Reportar complicaciones"],
    alertaPendiente: "Control post-quirúrgico no agendado",
  },
];

function getEtapasPorTipo(tipoViaje) {
  const tipo = (tipoViaje || "").toLowerCase();
  if (tipo.includes("quirúrgico") || tipo.includes("hospitalario") || tipo.includes("urgencia")) {
    return ETAPAS_HOSPITALARIO;
  }
  return ETAPAS_AMBULATORIO;
}

function calcularEstadoEtapas(etapasStatus, etapasConfig) {
  if (!etapasStatus || etapasStatus.length === 0) return etapasConfig.map((e) => ({ ...e, estado: "pendiente" }));
  let lastCompleted = -1;
  const completedSet = new Set();
  for (const es of etapasStatus) {
    if (es.completada) {
      completedSet.add(es.etapa);
      const idx = etapasConfig.findIndex((e) => e.key === es.etapa);
      if (idx > lastCompleted) lastCompleted = idx;
    }
  }
  return etapasConfig.map((e, i) => {
    if (completedSet.has(e.key)) return { ...e, estado: "completado" };
    if (i === lastCompleted + 1) return { ...e, estado: "actual" };
    return { ...e, estado: "pendiente" };
  });
}

function filtrarEventosPorEtapa(eventos, etapaKey) {
  if (!eventos) return [];
  const mapeo = {
    "Acceso": ["agenda", "búsqueda", "reserva", "cancelación", "cancelacion"],
    "Atención": ["consulta", "ambulatorio", "telemedicina"],
    "Diagnóstica": ["laboratorio", "lab", "imagen", "radiograf", "ecograf"],
    "Tratamiento": ["farmacia", "cirugía", "medicamento", "procedimiento"],
    "Financiero": ["pago", "bono", "financ", "copago"],
    "Seguimiento": ["seguimiento", "control", "encuesta", "crm", "contacto"],
  };
  const keywords = mapeo[etapaKey] || [];
  return eventos
    .filter((e) => keywords.some((k) => (e.ambito || "").toLowerCase().includes(k)))
    .sort((a, b) => (b.fecha || "").localeCompare(a.fecha || ""))
    .slice(0, 8);
}

export default function MapaViaje({ etapasStatus, proximoPaso, eventos, pasos, tipoViaje, paciente, onEstimarCostos }) {
  const [selectedEtapa, setSelectedEtapa] = useState(null);
  const etapasConfig = getEtapasPorTipo(tipoViaje);
  const etapas = calcularEstadoEtapas(etapasStatus, etapasConfig);
  const etapaActual = etapas.find((e) => e.estado === "actual");

  const handleNodeClick = (etapa) => {
    setSelectedEtapa(selectedEtapa?.key === etapa.key ? null : etapa);
  };

  const handleEstimarCostos = () => {
    if (onEstimarCostos) onEstimarCostos();
  };

  // Events and pending items for selected stage
  const eventosEtapa = selectedEtapa ? filtrarEventosPorEtapa(eventos, selectedEtapa.key) : [];
  const pasosEtapa = selectedEtapa && pasos
    ? pasos.filter((p) => p.etapa === selectedEtapa.key)
    : [];
  const pendientes = pasosEtapa.filter((p) => p.estado === "pendiente" || p.estado === "urgente");

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-5" data-testid="mapa-viaje">
      <div>
        <h3 className="text-lg font-bold text-slate-900">🗺️ Mapa de tu Viaje</h3>
        <p className="text-sm text-slate-500 mt-0.5">Haz clic en cada etapa para ver el detalle y recomendaciones</p>
      </div>

      {/* Desktop: horizontal flow */}
      <div className="hidden md:block">
        <div className="flex items-center justify-between">
          {etapas.map((etapa, i) => {
            const Icon = etapa.icon;
            const isCompleted = etapa.estado === "completado";
            const isCurrent = etapa.estado === "actual";
            const isPending = etapa.estado === "pendiente";
            const isSelected = selectedEtapa?.key === etapa.key;

            return (
              <div key={etapa.key} className="flex items-center flex-1">
                <div className="flex flex-col items-center flex-1">
                  <button
                    onClick={() => handleNodeClick(etapa)}
                    className={cn(
                      "relative h-16 w-16 rounded-2xl flex items-center justify-center transition-all duration-300 cursor-pointer",
                      isCompleted && "text-white shadow-lg hover:scale-110",
                      isCurrent && "text-white shadow-lg shadow-amber-300/50 ring-2 ring-amber-400 ring-offset-2",
                      isPending && "bg-slate-100 text-slate-400 hover:bg-slate-200 hover:scale-105",
                      isSelected && "ring-2 ring-offset-2 scale-110",
                      !isCurrent && isSelected && "ring-violet-500"
                    )}
                    style={isCompleted || isCurrent ? { backgroundColor: etapa.color } : {}}
                    aria-label={`Ver detalle de etapa ${etapa.label}`}
                  >
                    <Icon className="w-7 h-7" />
                    {isCompleted && (
                      <div className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-emerald-500 border-2 border-white flex items-center justify-center">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    {isCurrent && (
                      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
                        <div className="px-1.5 py-0.5 rounded bg-amber-500 text-[8px] font-bold text-white whitespace-nowrap">AQUÍ</div>
                      </div>
                    )}
                  </button>
                  <span className={cn("text-xs font-semibold mt-2", isCompleted ? "text-slate-900" : isCurrent ? "text-amber-700" : "text-slate-400")}>
                    {etapa.label}
                  </span>
                  <span className={cn("text-[10px] mt-0.5 text-center max-w-[90px]", isPending ? "text-slate-300" : "text-slate-500")}>
                    {etapa.desc}
                  </span>
                </div>
                {i < etapas.length - 1 && (
                  <div className="flex items-center px-1 -mt-6">
                    <div className={cn("h-0.5 w-6 lg:w-10", etapas[i + 1].estado !== "pendiente" ? "bg-slate-300" : "bg-slate-200 border-t border-dashed border-slate-300 h-0")} />
                    <ChevronRight className={cn("w-4 h-4 -ml-1", etapas[i + 1].estado !== "pendiente" ? "text-slate-400" : "text-slate-200")} />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical flow */}
      <div className="md:hidden space-y-2">
        {etapas.map((etapa) => {
          const Icon = etapa.icon;
          const isCompleted = etapa.estado === "completado";
          const isCurrent = etapa.estado === "actual";
          const isPending = etapa.estado === "pendiente";
          const isSelected = selectedEtapa?.key === etapa.key;
          return (
            <button
              key={etapa.key}
              onClick={() => handleNodeClick(etapa)}
              className={cn(
                "w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left",
                isCompleted && "border-emerald-200 bg-emerald-50",
                isCurrent && "border-amber-300 bg-amber-50 ring-1 ring-amber-300",
                isPending && "border-slate-100 bg-slate-50 opacity-60",
                isSelected && "ring-2 ring-violet-400"
              )}
            >
              <div className={cn("h-10 w-10 rounded-xl flex items-center justify-center shrink-0", isPending && "bg-slate-200 text-slate-400")}
                   style={(isCompleted || isCurrent) ? { backgroundColor: etapa.color, color: "white" } : {}}>
                <Icon className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <span className={cn("text-sm font-semibold", isPending ? "text-slate-400" : "text-slate-900")}>{etapa.label}</span>
                <span className={cn("text-xs ml-2", isPending ? "text-slate-300" : "text-slate-500")}>{etapa.desc}</span>
              </div>
              {isCompleted && <span className="text-xs font-bold text-emerald-600">✓</span>}
              {isCurrent && <span className="text-[10px] font-bold text-amber-600 bg-amber-200 px-2 py-0.5 rounded-full">AQUÍ</span>}
            </button>
          );
        })}
      </div>

      {/* Detail panel for selected stage */}
      {selectedEtapa && (
        <div className="rounded-xl border p-5 space-y-4 animate-in fade-in slide-in-from-top-2 duration-200"
             style={{ borderColor: selectedEtapa.color + "60", backgroundColor: selectedEtapa.color + "08" }}>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-xl flex items-center justify-center text-white" style={{ backgroundColor: selectedEtapa.color }}>
                <selectedEtapa.icon className="w-5 h-5" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-slate-900">{selectedEtapa.label}</h4>
                <span className={cn(
                  "text-[10px] font-bold uppercase px-2 py-0.5 rounded-full text-white",
                  selectedEtapa.estado === "completado" ? "bg-emerald-500" : selectedEtapa.estado === "actual" ? "bg-amber-500" : "bg-slate-400"
                )}>
                  {selectedEtapa.estado === "completado" ? "Completado" : selectedEtapa.estado === "actual" ? "En curso" : "Pendiente"}
                </span>
              </div>
            </div>
            <button onClick={() => setSelectedEtapa(null)} className="h-7 w-7 rounded-lg hover:bg-slate-200 flex items-center justify-center transition" aria-label="Cerrar detalle">
              <X className="w-4 h-4 text-slate-500" />
            </button>
          </div>

          {/* Alert for pending/current stages */}
          {selectedEtapa.estado !== "completado" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200">
              <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 shrink-0" />
              <div>
                <p className="text-xs font-semibold text-amber-700">{selectedEtapa.alertaPendiente}</p>
                {pendientes.length > 0 && (
                  <ul className="mt-1 space-y-1">
                    {pendientes.map((p) => (
                      <li key={p.id} className="text-xs text-amber-600 flex items-center gap-1">
                        <span className="w-1 h-1 rounded-full bg-amber-500 shrink-0" />
                        {p.titulo}
                        {p.diasDesde && <span className="text-[10px] text-amber-400 tabular-nums ml-1">({p.diasDesde}d)</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}

          {/* Completed alert */}
          {selectedEtapa.estado === "completado" && (
            <div className="flex items-start gap-2 p-3 rounded-lg bg-emerald-50 border border-emerald-200">
              <CheckCircle2 className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />
              <p className="text-xs text-emerald-700">Esta etapa está completada. Revisa los eventos recientes abajo.</p>
            </div>
          )}

          {/* Recent events for this stage */}
          {eventosEtapa.length > 0 && (
            <div>
              <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Últimos eventos</div>
              <div className="space-y-1.5">
                {eventosEtapa.slice(0, 5).map((e, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs">
                    <span className="text-[10px] text-slate-400 tabular-nums w-16 shrink-0">{e.fecha?.substring(5) || "—"}</span>
                    <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ backgroundColor: e.color || "#94A3B8" }} />
                    <span className="text-slate-700 truncate">{e.descripcion}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Recommendations */}
          <div>
            <div className="text-[10px] font-semibold text-slate-500 uppercase tracking-wider mb-2">Recomendaciones</div>
            <ul className="space-y-1.5">
              {selectedEtapa.recomendaciones.map((r, i) => (
                <li key={i} className="flex items-start gap-2 text-xs text-slate-700">
                  <span className="text-emerald-500 mt-0.5">→</span>
                  {r}
                </li>
              ))}
            </ul>
          </div>

          {/* Action buttons */}
          <div className="flex flex-wrap gap-2 pt-1">
            {selectedEtapa.estado !== "completado" && (
              <a
                href="https://agenda.ucchristus.cl"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg text-white transition hover:-translate-y-0.5"
                style={{ backgroundColor: selectedEtapa.color }}
              >
                <Calendar className="w-3.5 h-3.5" />
                Agendar hora
                <ExternalLink className="w-3 h-3" />
              </a>
            )}
            {selectedEtapa.estimable && (
              <button
                onClick={handleEstimarCostos}
                className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-white border border-slate-300 text-slate-700 hover:bg-slate-50 hover:-translate-y-0.5 transition"
              >
                <Calculator className="w-3.5 h-3.5" />
                Estimar costos
              </button>
            )}
          </div>
        </div>
      )}

      {/* Next step callout — only when no stage is selected */}
      {!selectedEtapa && (etapaActual || proximoPaso) && (
        <div className="rounded-xl border border-violet-200 bg-violet-50 p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-violet-500 flex items-center justify-center text-white shrink-0">
              {etapaActual ? <etapaActual.icon className="w-5 h-5" /> : <MapPin className="w-5 h-5" />}
            </div>
            <div className="flex-1">
              <div className="text-xs font-semibold text-violet-700 uppercase tracking-wider">Próximo paso</div>
              <p className="text-sm font-medium text-slate-900 mt-1">
                {proximoPaso?.titulo || `Completar etapa: ${etapaActual?.label}`}
              </p>
              {proximoPaso?.descripcion && (
                <p className="text-xs text-slate-600 mt-0.5">{proximoPaso.descripcion}</p>
              )}
              {proximoPaso?.accion && (
                <a href={proximoPaso.link || "https://agenda.ucchristus.cl"} target="_blank" rel="noopener noreferrer"
                   className="inline-flex items-center gap-1.5 mt-2 text-xs font-semibold text-violet-700 bg-white border border-violet-200 rounded-lg px-3 py-1.5 hover:bg-violet-100 hover:-translate-y-0.5 transition">
                  {proximoPaso.accion}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
